import { z } from "zod";

import type { BggConfigStatus } from "./BggConfigStatus";

export interface Settings {
	dateFormat: string;
	timeFormat: string;
	uiLanguage: string;
	currency: string;
	statistics: boolean;
	updateCheckEnabled: boolean;
	versionTrack: string;
	shelfOfShameEnabled: boolean;
	shelfOfShameMonthsLimit: number;
	publicUrl: string;
	gameNightsEnabled: boolean;
	rsvpAuthenticationEnabled: boolean;
	emailEnabled: boolean;
	ragEnabled: boolean;
	bggStatus: BggConfigStatus;
	bggApiKey: string | null;
	ai: AiSettings;
}

export interface AiSettings {
	provider: string;
	baseUrl: string;
	chatModel: string;
	/** A key is stored. The key itself never leaves the server. */
	hasApiKey: boolean;
	apiKey: string | null;
	fallbackProvider: string;
	fallbackBaseUrl: string;
	fallbackChatModel: string;
	/** Shown, never sent: moving it would invalidate every stored vector. */
	embeddingBaseUrl: string;
	embeddingModel: string;
	topK: number;
	/** Field names the environment pins; those inputs are read-only. */
	envLocked: string[];
}

export interface AiEndpointStatus {
	role: "primary" | "fallback";
	baseUrl: string;
	model: string;
	configured: boolean;
	reachable: boolean;
	inUse: boolean;
}

export const SettingsSchema = z.object({
	dateFormat: z
		.string({
			error: "settings:date-format.required",
		})
		.min(1, {
			message: "settings:date-format.required",
		}),
	timeFormat: z
		.string({
			error: "settings:time-format.required",
		})
		.min(1, {
			message: "settings:time-format.required",
		}),
	uiLanguage: z.string({
		error: "settings:ui-language.required",
	}),
	currency: z
		.string({
			error: "settings:currency.required",
		})
		.min(1, {
			message: "settings:currency.required",
		}),
	publicUrl: z.string(),
	statistics: z.boolean(),
	updateCheckEnabled: z.boolean(),
	versionTrack: z.string(),
	shelfOfShameEnabled: z.boolean(),
	shelfOfShameMonthsLimit: z.number().min(1),
	gameNightsEnabled: z.boolean(),
	rsvpAuthenticationEnabled: z.boolean(),
	bggApiKey: z.string().nullable(),
	ai: z.object({
		provider: z.string(),
		baseUrl: z.string(),
		chatModel: z.string(),
		hasApiKey: z.boolean(),
		apiKey: z.string().nullable(),
		fallbackProvider: z.string(),
		fallbackBaseUrl: z.string(),
		fallbackChatModel: z.string(),
		embeddingBaseUrl: z.string(),
		embeddingModel: z.string(),
		topK: z.number().min(1),
		envLocked: z.array(z.string()),
	}),
});
